import { Base } from '../base/index.js';
import { trapFocus } from '../../lib/trap-focus.js';

/**
 * Component's name.
 * @type {string}
 */
export const name = 'menu-item';

/**
 * Class MenuItem.
 */
export class MenuItem extends Base {
  /**
   * @type {import('../../types.d.ts').BaseConfig}
   */
  static config = {
    name,
  };

  /**
   *
   */
  connectedCallback() {
    this.getElements();
    this.isOpen = false;
  }

  /**
   * Drop the outside-click listener if the element is removed while open,
   * rather than leaving it on `document` indefinitely.
   */
  disconnectedCallback() {
    document.removeEventListener('click', this.onClickOutside);
  }

  /**
   * getElements
   */
  getElements() {
    /** @type {HTMLButtonElement} */
    this.btn = this.querySelector('.select-btn');
    /** @type {HTMLButtonElement} */
    this.backBtn = this.querySelector('.select-back-btn');
    /** @type {HTMLElement} */
    this.list = this.querySelector('.select-list');

    if (this.list && !this.list.hasAttribute('tabindex')) {
      this.list.setAttribute('tabindex', '-1');
    }

    this.btn.setAttribute('aria-expanded', 'false');

    this.btn.addEventListener('click', this.onBtnClick.bind(this));

    if (this.backBtn) {
      this.backBtn.addEventListener('click', this.close.bind(this));
    }

    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('focusout', this.onFocusout.bind(this));
  }

  /**
   * Whether the opened list behaves as a drill-down panel (mobile: it covers
   * its parent level and has a visible "Retour" button) rather than as a
   * plain dropdown (desktop, from 1024px: the back button is hidden by CSS).
   * Read from the rendered layout rather than a hardcoded breakpoint, so it
   * stays in sync with menu-item/index.css.
   * @returns {boolean}
   */
  isDrillDown() {
    return !!this.backBtn && this.backBtn.getClientRects().length > 0;
  }

  /**
   * onClick
   */
  onBtnClick() {
    if (this.isOpen) {
      return this.close();
    }

    return this.open();
  }

  /**
   * Close
   */
  close() {
    this.list.classList.remove('active');
    this.btn.classList.remove('active');
    this.btn.setAttribute('aria-expanded', 'false');
    this.isOpen = false;
    document.removeEventListener('click', this.onClickOutside);

    this.siblingItems?.forEach((el) => el.removeAttribute('inert'));
    this.siblingItems = null;

    // Only restore focus if it's still inside this menu item: close() is
    // also reached from onClickOutside, and unconditionally focusing here
    // would yank focus away from whatever the user actually clicked
    // elsewhere on the page (a link, a form field...) back to this menu's
    // trigger.
    if (this.contains(document.activeElement)) {
      this.previouslyFocused?.focus();
    }
    this.previouslyFocused = null;
  }

  /**
   * Open
   */
  open() {
    this.btn.classList.add('active');
    this.list.classList.add('active');
    this.btn.setAttribute('aria-expanded', 'true');
    this.isOpen = true;

    document.addEventListener('click', this.onClickOutside);

    // Save/restore focus locally (per instance) rather than via the
    // js-toolkit's saveActiveElement/untrapFocus shared module state: when
    // a MenuItem is nested inside another (drill-down submenus), both
    // instances would share the same "focusedBefore" reference and could
    // overwrite each other's restore target.
    this.previouslyFocused = document.activeElement;

    // Must be evaluated once the list is displayed (`.active` above), since
    // the back button lives inside it.
    this.drillDown = this.isDrillDown();

    // Desktop dropdown: disclosure pattern. Focus stays on the trigger
    // (announced as expanded) and Tab naturally continues into the list,
    // which directly follows it in the DOM. No focus trap here: the list
    // is not modal, and a trap would prevent reaching the next menu entry
    // with Tab (RGAA 12.9). Focusing the hidden back button would be a
    // no-op, and trapFocus() would then target it and swallow every Tab.
    if (!this.drillDown) {
      return;
    }

    // When this is a nested drill-down item (a submenu <li> inside its
    // parent's <ul>), inert the sibling <li> items: they stay visible
    // underneath the opened panel but must not remain reachable by Tab.
    const parentLi = this.closest('li');
    const list = parentLi?.parentElement;
    if (list) {
      this.siblingItems = Array.from(list.children).filter((el) => el !== parentLi);
      this.siblingItems.forEach((el) => el.setAttribute('inert', ''));
    }

    this.backBtn.focus();
  }

  /**
   * @param {object} event
   * @param {string} event.key
   */
  onKeyup(event) {
    if (this.isOpen && event.key === 'Escape') {
      event.stopPropagation();
      this.close();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    if (!this.isOpen || !this.drillDown) {
      return;
    }

    trapFocus(this.list, event);
    // Without this, Tab also reaches an ancestor MenuItem/Navbar that is
    // still open (nested drill-down submenus), which then traps focus on
    // its own, larger container too: whichever one runs last wins, so Tab
    // at the end of an open submenu lands on the parent panel's boundary
    // instead of looping within the submenu itself. Scoped to Tab alone so
    // other keydown handlers (e.g. a document-level one) still run.
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }

  /**
   * Desktop dropdown only: close once keyboard focus has moved outside of
   * this item (e.g. Tab past its last link, onto the next menu entry), so
   * the open panel never lingers over the newly focused element (RGAA 10.7).
   * A null relatedTarget (click on a non-focusable area, window blur) is
   * left to onClickOutside.
   * @param {FocusEvent} event
   */
  onFocusout(event) {
    if (!this.isOpen || this.drillDown) {
      return;
    }

    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.close();
    }
  }

  /**
   * on Click Outside
   *
   * A class field arrow function rather than a prototype method bound in
   * connectedCallback(): connectedCallback() re-runs every time the custom
   * element is reconnected to the DOM, which would otherwise re-bind to a
   * new function reference each time and strand the previous one (added to
   * `document` in a prior open()) with nothing able to remove it.
   * @param {object} event
   * @param {*} event.target
   */
  onClickOutside = ({ target }) => {
    if (!this.contains(target)) {
      this.close();
    }
  };
}

export default MenuItem;
